import { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, Clock, Calendar, Upload } from 'lucide-react';
import { AdminLayout } from './AdminDashboard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyService = { name: '', description: '', price: 0, duration_minutes: 60, image_urls: [], is_active: true };

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editService, setEditService] = useState(null);
  const [form, setForm] = useState({ ...emptyService });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  // Slots
  const [slotOpen, setSlotOpen] = useState(null);
  const [slotDate, setSlotDate] = useState('');
  const [slotPairs, setSlotPairs] = useState([{ start_time: '09:00', end_time: '10:00' }]);
  const [existingSlots, setExistingSlots] = useState([]);

  const fetch = () => { setLoading(true); axios.get(`${API}/admin/services`, { withCredentials: true }).then(r => setServices(r.data.services)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { fetch(); }, []);

  const openNew = () => { setEditService(null); setForm({ ...emptyService }); setDialogOpen(true); };
  const openEdit = (s) => { setEditService(s); setForm({ ...s, image_urls: s.image_urls || [] }); setDialogOpen(true); };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) { toast.error('Only JPG, PNG, WebP, GIF files allowed'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
    setUploading(true);
    try {
      const formData = new FormData(); formData.append('file', file);
      const { data } = await axios.post(`${API}/upload/image`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, image_urls: [...(prev.image_urls || []), data.image_url] }));
      toast.success('Image uploaded!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); } finally { setUploading(false); }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!form.name || form.price <= 0) { toast.error('Fill required fields'); return; }
    setSaving(true);
    const payload = { ...form, video_urls: [] }; // Explicitly clear any legacy videos

    try {
      if (editService) await axios.put(`${API}/admin/services/${editService.id}`, payload, { withCredentials: true });
      else await axios.post(`${API}/admin/services`, payload, { withCredentials: true });
      toast.success(editService ? 'Updated' : 'Created'); setDialogOpen(false); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => { if (!window.confirm('Delete?')) return; try { await axios.delete(`${API}/admin/services/${id}`, { withCredentials: true }); toast.success('Deleted'); fetch(); } catch { toast.error('Failed'); } };

  const openSlots = async (service) => {
    setSlotOpen(service);
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
    setSlotDate(tomorrow.toISOString().split('T')[0]);
    setSlotPairs([{ start_time: '09:00', end_time: '10:00' }]);
    const { data } = await axios.get(`${API}/admin/slots?service_id=${service.id}`, { withCredentials: true });
    setExistingSlots(data.slots);
  };

  const addSlotPair = () => setSlotPairs([...slotPairs, { start_time: '', end_time: '' }]);

  const createSlots = async () => {
    if (!slotDate || slotPairs.some(s => !s.start_time || !s.end_time)) { toast.error('Fill all slots'); return; }
    try {
      await axios.post(`${API}/admin/slots`, { service_id: slotOpen.id, date: slotDate, slots: slotPairs }, { withCredentials: true });
      toast.success('Slots created');
      const { data } = await axios.get(`${API}/admin/slots?service_id=${slotOpen.id}`, { withCredentials: true });
      setExistingSlots(data.slots);
      setSlotPairs([{ start_time: '09:00', end_time: '10:00' }]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteSlot = async (slotId) => {
    try { await axios.delete(`${API}/admin/slots/${slotId}`, { withCredentials: true }); toast.success('Deleted'); setExistingSlots(prev => prev.filter(s => s.id !== slotId)); } catch { toast.error('Failed'); }
  };

  return (
    <AdminLayout active="/admin/services">
      <div data-testid="admin-services">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-3xl font-bold text-dark-brown">Services</h1>
          <Button onClick={openNew} className="bg-rust hover:bg-rust/90 text-cream font-body rounded-full" data-testid="add-service-btn"><Plus className="w-4 h-4 mr-2" /> Add Service</Button>
        </div>
        {loading ? <div className="flex justify-center py-20"><div className="spinner"></div></div> : (
          <div className="grid md:grid-cols-2 gap-4">
            {services.map(s => (
              <div key={s.id} className="bg-white rounded-xl border border-border-custom p-5" data-testid={`admin-service-${s.id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <h3 className="font-body font-medium text-dark-brown">{s.name}</h3>
                    <p className="font-body text-xs text-light-brown line-clamp-2" style={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}>{s.description}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(s)} className="p-2 rounded-lg hover:bg-pale"><Pencil className="w-4 h-4 text-mid-brown" /></button>
                    <button onClick={() => handleDelete(s.id)} className="p-2 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-500" /></button>
                  </div>
                </div>
                <div className="flex items-center gap-4 mb-3">
                  <span className="font-bold text-[#212121]" style={{ fontFamily: 'Roboto, sans-serif', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{'\u20B9'}{s.price}</span>
                  <span className="font-body text-xs text-mid-brown flex items-center gap-1"><Clock className="w-3 h-3" />{s.duration_minutes}min</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${s.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{s.is_active ? 'Active' : 'Inactive'}</span>
                </div>
                <Button size="sm" variant="outline" onClick={() => openSlots(s)} className="font-body text-xs rounded-full" data-testid={`manage-slots-${s.id}`}>
                  <Calendar className="w-3 h-3 mr-1" /> Manage Time Slots
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Service Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader><DialogTitle className="font-heading text-xl text-dark-brown">{editService ? 'Edit' : 'New'} Service</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div><Label className="font-body text-sm">Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 bg-cream/50" /></div>
              <div><Label className="font-body text-sm">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="mt-1 bg-cream/50" rows={3} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="font-body text-sm">Price *</Label><Input type="number" value={form.price} onChange={e => setForm({...form, price: parseFloat(e.target.value)||0})} className="mt-1 bg-cream/50" /></div>
                <div><Label className="font-body text-sm">Duration (min)</Label><Input type="number" value={form.duration_minutes} onChange={e => setForm({...form, duration_minutes: parseInt(e.target.value)||60})} className="mt-1 bg-cream/50" /></div>
              </div>
              <div>
                <Label className="font-body text-sm mb-2 block">Service Images</Label>
                <div className="flex flex-wrap gap-3">
                  {(form.image_urls || []).map((url, idx) => (
                    <div key={idx} className="relative group w-20 h-20">
                      <img src={url} alt="" className="w-full h-full object-cover rounded-lg border border-border-custom" />
                      <button type="button" onClick={() => setForm(f => ({...f, image_urls: f.image_urls.filter((_, i) => i !== idx)}))} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity shadow-sm z-10">✕</button>
                    </div>
                  ))}
                  <button type="button" disabled={uploading} onClick={() => fileInputRef.current?.click()} className="w-20 h-20 border-2 border-dashed border-border-custom rounded-lg flex flex-col items-center justify-center text-mid-brown hover:bg-pale hover:border-rust transition-colors" data-testid="upload-service-img-btn">
                    {uploading ? <div className="spinner w-4 h-4"></div> : <><Upload className="w-5 h-5 mb-1" /><span className="text-[10px] font-body">Upload</span></>}
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleImageUpload} data-testid="service-file-input" />
                </div>
              </div>
              <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} /><Label className="font-body text-sm">Active</Label></div>
              <Button onClick={handleSave} disabled={saving} className="w-full bg-rust hover:bg-rust/90 text-cream font-body rounded-full" data-testid="save-service-btn">{saving ? 'Saving...' : editService ? 'Update' : 'Create'}</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Slots Dialog */}
        <Dialog open={!!slotOpen} onOpenChange={() => setSlotOpen(null)}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader><DialogTitle className="font-heading text-xl text-dark-brown">Time Slots: {slotOpen?.name}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Create Slots */}
              <div className="bg-pale rounded-xl p-4">
                <h4 className="font-body font-medium text-dark-brown mb-3">Add New Slots</h4>
                <div><Label className="font-body text-sm">Date</Label><Input type="date" value={slotDate} onChange={e => setSlotDate(e.target.value)} className="mt-1 bg-white" /></div>
                <div className="mt-3 space-y-2">
                  {slotPairs.map((sp, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input type="time" value={sp.start_time} onChange={e => { const n = [...slotPairs]; n[i].start_time = e.target.value; setSlotPairs(n); }} className="bg-white flex-1" />
                      <span className="text-xs text-light-brown">to</span>
                      <Input type="time" value={sp.end_time} onChange={e => { const n = [...slotPairs]; n[i].end_time = e.target.value; setSlotPairs(n); }} className="bg-white flex-1" />
                      {slotPairs.length > 1 && <button onClick={() => setSlotPairs(slotPairs.filter((_,j) => j !== i))} className="text-red-500 text-xs">Remove</button>}
                    </div>
                  ))}
                  <button onClick={addSlotPair} className="text-xs text-rust font-body hover:underline">+ Add another slot</button>
                </div>
                <Button onClick={createSlots} size="sm" className="mt-3 bg-rust text-cream font-body rounded-full" data-testid="create-slots-btn">Create Slots</Button>
              </div>

              {/* Existing Slots */}
              <div>
                <h4 className="font-body font-medium text-dark-brown mb-3">Existing Slots ({existingSlots.length})</h4>
                {existingSlots.length === 0 ? <p className="font-body text-sm text-light-brown">No slots created yet</p> : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {existingSlots.map(sl => (
                      <div key={sl.id} className="flex items-center justify-between bg-white rounded-lg border border-border-custom px-3 py-2">
                        <div className="font-body text-xs"><span className="text-dark-brown font-medium">{sl.date}</span> <span className="text-mid-brown">{sl.start_time}-{sl.end_time}</span></div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${sl.status === 'available' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{sl.status}</span>
                          <button onClick={() => deleteSlot(sl.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
